import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Select from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface BlockCondition {
  field: string;
  op: string;
  value: string;
}

export interface BlockState {
  measure: string;
  field: string;
  conditions: BlockCondition[];
}

export interface FieldGroup {
  name: string;
  fields: { name: string; type?: string; table?: string; description?: string }[];
}

const MEASURES = ['COUNT DISTINCT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX'];
const OPS = ['within last', 'on or after', 'before', '=', '≠', '≥', '≤'];

const OP_SQL: Record<string, string> = {
  'on or after': '>=', before: '<', '=': '=', '≠': '<>', '≥': '>=', '≤': '<=',
};

/**
 * The unqualified expression these blocks stand for.
 *
 * Kept in step with `definitions/drafter.py::blocks_to_formula` on purpose:
 * the preview is a promise about what the server will build, and a preview
 * that reads differently from the stored formula is worse than no preview.
 */
export function blocksToPreview(b: BlockState): string {
  const agg = b.measure === 'COUNT DISTINCT'
    ? `COUNT(DISTINCT ${b.field || '…'})`
    : `${b.measure}(${b.field || '…'})`;
  const clauses = b.conditions
    .filter((c) => c.field && c.value)
    .map((c) => (c.op === 'within last'
      ? `${c.field} >= CURRENT_DATE - INTERVAL '${c.value}'`
      : `${c.field} ${OP_SQL[c.op] || c.op} ${c.value}`));
  return clauses.length ? `${agg} FILTER (WHERE ${clauses.join(' AND ')})` : agg;
}

/** Field picker + measure + conditions — no SQL, no prose to misread. */
export default function DefinitionBuildingBlocks({
  fieldGroups, blocks, onChange,
}: {
  fieldGroups: FieldGroup[];
  blocks: BlockState;
  onChange: (b: BlockState) => void;
}) {
  // `null` = closed, `-1` = the measure's field, `>=0` = that condition's field.
  const [picker, setPicker] = React.useState<{ index: number; anchor: HTMLElement } | null>(null);
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return fieldGroups
      .map((g) => ({ ...g, fields: g.fields.filter((f) => !q || f.name.toLowerCase().includes(q)) }))
      .filter((g) => g.fields.length > 0);
  }, [fieldGroups, search]);

  const pick = (name: string) => {
    if (!picker) return;
    if (picker.index < 0) onChange({ ...blocks, field: name });
    else {
      onChange({
        ...blocks,
        conditions: blocks.conditions.map((c, i) => (i === picker.index ? { ...c, field: name } : c)),
      });
    }
    setPicker(null);
    setSearch('');
  };

  const fieldButton = (value: string, index: number) => (
    <Button
      size="small"
      variant="outlined"
      endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
      onClick={(e) => { setPicker({ index, anchor: e.currentTarget }); setSearch(''); }}
      sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: 12, minWidth: 0 }}
    >
      {value || 'pick a field'}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ width: 56 }}>MEASURE</Typography>
        <Select
          size="small"
          value={blocks.measure}
          onChange={(e) => onChange({ ...blocks, measure: String(e.target.value) })}
          sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
        >
          {MEASURES.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 12 }}>{m}</MenuItem>)}
        </Select>
        {fieldButton(blocks.field, -1)}
      </Box>

      {blocks.conditions.map((c, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 56 }}>
            {i === 0 ? 'WHERE' : 'AND'}
          </Typography>
          {fieldButton(c.field, i)}
          <Select
            size="small"
            value={c.op}
            onChange={(e) => onChange({
              ...blocks,
              conditions: blocks.conditions.map((cc, ci) => (ci === i ? { ...cc, op: String(e.target.value) } : cc)),
            })}
            sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {OPS.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 12 }}>{o}</MenuItem>)}
          </Select>
          <TextField
            size="small"
            value={c.value}
            placeholder="30 days"
            onChange={(e) => onChange({
              ...blocks,
              conditions: blocks.conditions.map((cc, ci) => (ci === i ? { ...cc, value: e.target.value } : cc)),
            })}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12, padding: '6px 8px' } }}
            sx={{ width: 140 }}
          />
          {blocks.conditions.length > 1 && (
            <IconButton
              size="small"
              onClick={() => onChange({
                ...blocks,
                conditions: blocks.conditions.filter((_, ci) => ci !== i),
              })}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => onChange({
            ...blocks,
            conditions: [...blocks.conditions, { field: '', op: '=', value: '' }],
          })}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          Add condition
        </Button>
      </Box>

      <Popover
        open={!!picker}
        anchorEl={picker?.anchor || null}
        onClose={() => { setPicker(null); setSearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, width: 320 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search fields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Box sx={{ maxHeight: 280, overflow: 'auto', mt: 1 }}>
            {filtered.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                No fields match.
              </Typography>
            )}
            {filtered.map((g) => (
              <MenuList key={g.name} dense subheader={
                <ListSubheader disableSticky sx={{ fontSize: 11, lineHeight: '24px' }}>
                  {g.name}
                </ListSubheader>
              }>
                {g.fields.map((f) => (
                  <MenuItem key={`${g.name}:${f.name}`} onClick={() => pick(f.name)} sx={{ fontSize: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span style={{ fontFamily: 'monospace' }}>{f.name}</span>
                      <Typography variant="caption" color="text.disabled" noWrap>
                        {[f.type, f.table].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
